import { MAX_VOICE_BYTES, VOICE_RECORDING_SECONDS, VOICE_TIMEOUT_MS } from '@/_data/voiceInput';
import { isVoiceTranscript } from '@/_pure/voiceInput';
import type { VoiceTranscript } from '@/_contract/voiceInput';
import { createMicrophoneTestController } from './microphoneTestController';
import { chatInputBlockReason, type createAgentChatController } from './agentChatController';
import { voiceDiagnostic, diagnosticErrorType } from './voiceDiagnostics';

type VoiceState = {
    enabled: boolean;
    configuration: 'loading' | 'ready' | 'error';
    phase: 'idle' | 'requesting' | 'recording' | 'stopping' | 'ready' | 'transcribing' | 'error';
    seconds: number;
    level: number;
    error?: string;
    result?: VoiceTranscript;
};

export function createVoiceInputController(chat: ReturnType<typeof createAgentChatController>,
    microphone = createMicrophoneTestController(undefined, { seconds: VOICE_RECORDING_SECONDS, bytes: MAX_VOICE_BYTES }),
    request: typeof fetch = (...args) => fetch(...args)) {
    let state: VoiceState = { enabled: false, configuration: 'loading', phase: 'idle', seconds: 0, level: 0 };
    const listeners = new Set<() => void>();
    let generation = 0;
    let upload: AbortController | undefined;
    const update = (patch: Partial<VoiceState>) => {
        if (patch.phase && patch.phase !== state.phase) voiceDiagnostic('voice-phase', { phase: patch.phase });
        state = { ...state, ...patch };
        listeners.forEach(listener => listener());
    };
    const cancel = () => {
        generation++;
        upload?.abort();
        upload = undefined;
        microphone.dispose();
        chat.finishVoiceInput();
        update({ phase: 'idle', seconds: 0, level: 0, error: undefined });
    };
    return {
        getSnapshot: () => state,
        subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
        start() {
            const active = new AbortController();
            const off = microphone.subscribe(() => {
                const mic = microphone.getSnapshot();
                if (state.phase === 'transcribing') return;
                if (mic.error) update({ phase: 'error', error: mic.error, level: 0 });
                else if (mic.phase === 'recording' || mic.phase === 'stopping') update({ phase: mic.phase, seconds: mic.seconds, level: mic.level });
                else if (microphone.getRecording()) update({ phase: 'ready', seconds: mic.seconds, level: 0 });
            });
            void request('/api/agent/transcribe', { signal: active.signal, cache: 'no-store' }).then(async response => {
                const body: unknown = await response.json();
                if (!response.ok || !body || typeof body !== 'object' || !('enabled' in body) || typeof body.enabled !== 'boolean') {
                    const reason = body && typeof body === 'object' && 'error' in body && typeof body.error === 'string' ? `${body.error} ` : '';
                    throw new Error(`Voice input configuration unavailable. ${reason}Reload to retry.`);
                }
                if (!active.signal.aborted) update({ enabled: body.enabled, configuration: 'ready' });
            }).catch(error => {
                if (!active.signal.aborted) {
                    console.error('[agent-voice] Configuration failed.');
                    update({ configuration: 'error', error: error instanceof Error ? error.message : 'Voice input configuration failed.' });
                }
            });
            return () => { active.abort(); off(); cancel(); };
        },
        async record() {
            if (!state.enabled || !['idle', 'error'].includes(state.phase)) return;
            cancel();
            if (!chat.beginVoiceInput()) {
                update({ phase: 'error', error: chatInputBlockReason(chat.getSnapshot()) ?? 'Voice input could not reserve the draft. Retry.' });
                return;
            }
            const current = generation;
            update({ phase: 'requesting', result: undefined });
            await microphone.enable();
            if (current !== generation) return;
            if (microphone.getSnapshot().phase === 'ready') microphone.record();
        },
        stop: () => microphone.stop(),
        cancel,
        async transcribe() {
            const blob = microphone.getRecording();
            if (!blob || !['ready', 'error'].includes(state.phase)) return;
            const current = generation;
            const requestId = crypto.randomUUID();
            const started = Date.now();
            const pending = new AbortController();
            upload = pending;
            update({ phase: 'transcribing', error: undefined });
            voiceDiagnostic('transcribe-start', { requestId, bytes: blob.size });
            try {
                const response = await request('/api/agent/transcribe', {
                    method: 'POST', headers: { 'Content-Type': blob.type, 'X-Voice-Request-Id': requestId }, body: blob,
                    signal: AbortSignal.any([pending.signal, AbortSignal.timeout(VOICE_TIMEOUT_MS)]),
                });
                voiceDiagnostic('transcribe-response', { requestId, status: response.status, elapsedMs: Date.now() - started });
                const body: unknown = await response.json();
                if (current !== generation) return;
                if (!response.ok) throw new Error(body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
                    ? body.error : 'Transcription failed. Retry or cancel; draft unchanged.');
                if (!isVoiceTranscript(body)) throw new Error('Invalid transcription response; draft unchanged.');
                chat.finishVoiceInput(body.text);
                microphone.dispose();
                update({ phase: 'idle', seconds: 0, level: 0, result: body, error: undefined });
            } catch (error) {
                voiceDiagnostic('transcribe-error', { requestId, elapsedMs: Date.now() - started,
                    errorType: diagnosticErrorType(error), aborted: pending.signal.aborted });
                if (current !== generation) return;
                console.error('[agent-voice] Transcription failed.');
                update({ phase: 'error', error: error instanceof Error ? error.message : 'Transcription failed. Retry or cancel; draft unchanged.' });
            } finally { if (upload === pending) upload = undefined; }
        },
        hasRecording: () => !!microphone.getRecording(),
    };
}

export function voiceInputSummary(state: VoiceState) {
    return state.error ?? (state.phase === 'idle'
        ? state.result ? `Added to draft (${state.result.language}, ${(state.result.elapsedMs / 1000).toFixed(1)}s processing). Review/edit, then Send.`
            : `Record up to ${VOICE_RECORDING_SECONDS}s. Audio goes only to Forge; review the transcript before Send.`
        : state.phase === 'recording' ? `Recording ${state.seconds.toFixed(1)}s / ${VOICE_RECORDING_SECONDS}s | Level ${state.level}%`
            : state.phase === 'ready' ? 'Recording ready. Transcribe into draft or Cancel.'
                : state.phase === 'transcribing' ? 'Transcribing on Forge... Cancel discards the result; draft is unchanged.'
                    : `Voice: ${state.phase}`);
}
